import * as React from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '../../utils/cn'

/**
 * JPG Academy Toast
 *
 * Transient notification for success, error, warning, and info states.
 * Auto-dismisses after a configurable duration. Positioned bottom-right.
 */

export type ToastVariant = 'default' | 'success' | 'error' | 'warning' | 'info'

export interface ToastData {
	id: string
	variant: ToastVariant
	title: string
	description?: string
	duration?: number
	action?: { label: string; onClick: () => void }
}

const variantStyles: Record<ToastVariant, { container: string; icon: string }> = {
	default: { container: 'bg-navy text-cream-50 border-navy-600', icon: 'text-gold' },
	success: { container: 'bg-white border-success/25 text-foreground', icon: 'text-success' },
	error: { container: 'bg-white border-error/25 text-foreground', icon: 'text-error' },
	warning: { container: 'bg-white border-warning/25 text-foreground', icon: 'text-warning' },
	info: { container: 'bg-white border-info/25 text-foreground', icon: 'text-info' },
}

const variantIcons: Record<ToastVariant, React.ReactNode> = {
	default: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M12 16v-4" /><path d="M12 8h.01" /></svg>,
	success: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>,
	error: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="m15 9-6 6" /><path d="m9 9 6 6" /></svg>,
	warning: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" /><path d="M12 9v4" /><path d="M12 17h.01" /></svg>,
	info: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M12 16v-4" /><path d="M12 8h.01" /></svg>,
}

function ToastItem({ data, onDismiss }: { data: ToastData; onDismiss: (id: string) => void }) {
	const styles = variantStyles[data.variant]
	const icon = variantIcons[data.variant]
	const duration = data.duration ?? (data.variant === 'error' ? 8000 : 5000)

	React.useEffect(() => {
		const timer = setTimeout(() => onDismiss(data.id), duration)
		return () => clearTimeout(timer)
	}, [data.id, duration, onDismiss])

	return (
		<motion.div
			role="status"
			aria-live="polite"
			className={cn('flex items-start gap-3 w-80 p-4 rounded-lg border shadow-lg pointer-events-auto', styles.container)}
			layout
			initial={{ opacity: 0, x: 80, scale: 0.95 }}
			animate={{ opacity: 1, x: 0, scale: 1 }}
			exit={{ opacity: 0, x: 80, scale: 0.95 }}
			transition={{ type: 'spring', stiffness: 400, damping: 30 }}
		>
			<motion.div className={cn('flex-shrink-0 mt-0.5', styles.icon)} initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 400, damping: 20, delay: 0.1 }}>
				{icon}
			</motion.div>
			<div className="flex-1 min-w-0">
				<p className="text-sm font-semibold font-ui leading-tight">{data.title}</p>
				{data.description && <p className="text-xs mt-1 opacity-70 font-ui leading-relaxed">{data.description}</p>}
				{data.action && <button onClick={data.action.onClick} className="text-xs font-semibold mt-2 underline underline-offset-2 decoration-current/40 hover:decoration-current transition-all duration-fast">{data.action.label}</button>}
			</div>
			<motion.button onClick={() => onDismiss(data.id)} className="flex-shrink-0 opacity-40 hover:opacity-100 transition-opacity duration-fast p-0.5 rounded" aria-label="Dismiss notification" whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
				<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg>
			</motion.button>
		</motion.div>
	)
}

interface ToastContextValue {
	toasts: ToastData[]
	addToast: (toast: Omit<ToastData, 'id'>) => string
	dismiss: (id: string) => void
}
const ToastContext = React.createContext<ToastContextValue | null>(null)
let toastCounter = 0
const generateId = () => `toast-${++toastCounter}-${Date.now()}`

export interface ToastProviderProps {
	children: React.ReactNode
	position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left'
}
const positionStyles: Record<string, string> = {
	'bottom-right': 'bottom-4 right-4', 'bottom-left': 'bottom-4 left-4', 'top-right': 'top-4 right-4', 'top-left': 'top-4 left-4',
}

export function ToastProvider({ children, position = 'bottom-right' }: ToastProviderProps) {
	const [toasts, setToasts] = React.useState<ToastData[]>([])
	const addToast = React.useCallback((toast: Omit<ToastData, 'id'>) => {
		const id = generateId(); setToasts(prev => [...prev, { ...toast, id }]); return id
	}, [])
	const dismiss = React.useCallback((id: string) => setToasts(prev => prev.filter(t => t.id !== id)), [])
	const ctx = React.useMemo(() => ({ toasts, addToast, dismiss }), [toasts, addToast, dismiss])
	React.useEffect(() => { imperativeCtx = ctx; return () => { imperativeCtx = null } }, [ctx])

	return (
		<ToastContext.Provider value={ctx}>
			{children}
			<div aria-label="Notifications" className={cn('fixed z-toast flex flex-col gap-2 pointer-events-none', positionStyles[position])}>
				<AnimatePresence mode="popLayout">
					{toasts.map(toast => <ToastItem key={toast.id} data={toast} onDismiss={dismiss} />)}
				</AnimatePresence>
			</div>
		</ToastContext.Provider>
	)
}

export function useToast() {
	const ctx = React.useContext(ToastContext)
	if (!ctx) throw new Error('useToast must be used within a <ToastProvider>')
	return ctx
}

export const toast = {
	success(title: string, description?: string, duration?: number) { return imperativeAdd({ variant: 'success', title, description, duration }) },
	error(title: string, description?: string, duration?: number) { return imperativeAdd({ variant: 'error', title, description, duration }) },
	warning(title: string, description?: string, duration?: number) { return imperativeAdd({ variant: 'warning', title, description, duration }) },
	info(title: string, description?: string, duration?: number) { return imperativeAdd({ variant: 'info', title, description, duration }) },
	default(title: string, description?: string, duration?: number) { return imperativeAdd({ variant: 'default', title, description, duration }) },
	dismiss(id: string) { imperativeDismiss(id) },
}

let imperativeCtx: ToastContextValue | null = null
function imperativeAdd(toast: Omit<ToastData, 'id'>) {
	if (!imperativeCtx) {
		console.warn('[JPG Design System] Using imperative toast without <ToastProvider>. Wrap your app with <ToastProvider> for toast support.')
		return ''
	}
	return imperativeCtx.addToast(toast)
}
function imperativeDismiss(id: string) { imperativeCtx?.dismiss(id) }
export function __setImperativeContext(ctx: ToastContextValue) { imperativeCtx = ctx }
